import clsx from 'clsx';

import { useGridSpacing } from '../hooks/useGridSpacing';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type SvgScaleLegendProps = {
  /**
   * Number of target grid lines used to compute the scale distance.
   * Should match the `targetLines` prop passed to `<SvgGrid>` so the
   * legend reflects the actual grid spacing.
   * @default 20
   */
  targetLines?: number;
  /** Additional CSS classes for the legend container */
  className?: string;
};

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Scale bar legend that displays the current grid spacing as a visual ruler.
 *
 * Renders as an HTML overlay positioned at the bottom-left of the canvas.
 * The distance adapts to the current zoom level, always showing a "nice"
 * round value (1, 2, 5 × 10^n meters). Must be placed inside an `<SvgCanvas>`.
 *
 * Reads zoom transform and bounds from `SvgContext` via `useGridSpacing`.
 *
 * @example
 * ```tsx
 * <SvgCanvas overlay={<SvgScaleLegend /> boundedByGeometries={geometries} title="Blast visualization">
 *   <SvgGrid />
 *   <DrillHolesLayer holes={drillHoles} />
 * </SvgCanvas>
 * ```
 */
const SvgScaleLegend = ({ targetLines, className }: SvgScaleLegendProps) => {
  const { spacing } = useGridSpacing({ targetLines });
  if (spacing === 0) return null;

  const label = spacing >= 1000 ? `${spacing / 1000} km` : `${spacing} m`;

  return (
    <div
      className={clsx(
        'absolute inset-s-16 bottom-4 flex flex-col items-start gap-1 rounded-sm border border-border/30 bg-background px-3 py-2 shadow-sm backdrop-blur-sm',
        className,
      )}
      role="status"
      aria-label={`Scale: ${label}`}
    >
      <div className="flex items-center justify-center gap-1">
        <span className="text-muted-foreground text-xs leading-none">{label}</span>
        <div className="flex h-3 w-23 flex-col items-stretch border-2 border-border border-t-0" />
      </div>
    </div>
  );
};
SvgScaleLegend.displayName = 'SvgScaleLegend';

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export type { SvgScaleLegendProps };
export { SvgScaleLegend };
